import { useCreateAndOpenProject } from '@heidi/agents';
import { useCallback, useRef } from 'react';

import { useLatestRef } from '@/hooks/useLatestRef';

type SeededCreateArgs = Parameters<
  ReturnType<typeof useCreateAndOpenProject>['createAndOpenProject']
>[0];
type SeededCreateGuard = NonNullable<
  Parameters<typeof useCreateAndOpenProject>[0]
>['guard'];

interface UseObjectWorkspaceSeededCreateOptions {
  guard?: SeededCreateGuard;
  /** Adopt the new project in the tab; return whether it was bound. */
  onCreated: (projectId: string) => boolean;
  onCreateError: () => void;
}

/**
 * A create that posts its own seeded opening turn, awaitable like the in-place
 * create: resolves whether the tab bound the project, `null` when the guard
 * refused it, and rejects when the POST fails so ProjectChat restores the draft.
 */
export function useObjectWorkspaceSeededCreate({
  guard,
  onCreated,
  onCreateError,
}: UseObjectWorkspaceSeededCreateOptions) {
  const onCreatedRef = useLatestRef(onCreated);
  const onCreateErrorRef = useLatestRef(onCreateError);
  const settleRef = useRef<{
    resolve: (bound: boolean) => void;
    reject: (error: unknown) => void;
  } | null>(null);
  const takeSettler = useCallback(() => {
    const pending = settleRef.current;
    settleRef.current = null;
    return pending;
  }, []);

  const handleProjectCreated = useCallback(
    (projectId: string): false => {
      const bound = onCreatedRef.current(projectId);
      takeSettler()?.resolve(bound);
      // The object workspace owns the mounted chat and URL. The create hook has
      // already seeded the opening turn, so suppress its normal route handoff.
      return false;
    },
    [onCreatedRef, takeSettler]
  );
  const handleCreateError = useCallback(
    (error: unknown) => {
      onCreateErrorRef.current();
      takeSettler()?.reject(error);
    },
    [onCreateErrorRef, takeSettler]
  );
  const { createAndOpenProject, isSubmitting } = useCreateAndOpenProject({
    onProjectCreated: handleProjectCreated,
    onCreateError: handleCreateError,
    guard,
  });

  const createSeeded = useCallback(
    (args: SeededCreateArgs) =>
      new Promise<boolean | null>((resolve, reject) => {
        settleRef.current = { resolve, reject };
        if (!createAndOpenProject(args)) {
          settleRef.current = null;
          resolve(null);
        }
      }),
    [createAndOpenProject]
  );

  return { createSeeded, isSubmitting };
}
